import React from 'react';
import { Badge } from '@aidkitorg/component-library';
import { FilterGroup, FilterCondition } from '@aidkitorg/types/lib/admin-panel/filters';

export const operatorLabels: Record<string, string> = {
  equals: '=',
  not_equals: '≠',
  contains: 'contains',
  not_contains: 'does not contain',
  empty: 'is empty',
  not_empty: 'is not empty',
  greater_than: '>',
  less_than: '<',
  after: 'after',
  before: 'before',
  exists: 'exists',
  not_exists: 'does not exist'
};

export const formatFieldName = (field: string, fieldLabels?: Record<string, string>): string => {
  if (fieldLabels && fieldLabels[field]) {
    return fieldLabels[field];
  }
  // Convert field names like "department" or "__synthetic_uid__" to readable format
  if (field.startsWith('__') && field.endsWith('__')) {
    return field.replace(/__/g, '').replace(/_/g, ' ');
  }
  return field.replace(/_/g, ' ');
};

export const formatCondition = (condition: FilterCondition, fieldLabels?: Record<string, string>): string => {
  const fieldName = formatFieldName(condition.field, fieldLabels);
  const operator = operatorLabels[condition.operator] || condition.operator;

  // For operators that don't need a value
  if (condition.operator === 'empty' || condition.operator === 'not_empty' ||
      condition.operator === 'exists' || condition.operator === 'not_exists') {
    return `${fieldName} ${operator}`;
  }

  // For operators with values
  if (condition.value) {
    return `${fieldName} ${operator} "${condition.value}"`;
  }

  return `${fieldName} ${operator}`;
};

export const renderFilterGroup = (
  group: FilterGroup,
  fieldLabels?: Record<string, string>,
  isNested: boolean = false,
  size: 'default' | 'small' = 'default'
): React.ReactNode[] => {
  const badges: React.ReactNode[] = [];

  // Add opening parenthesis for nested groups
  if (isNested) {
    badges.push(
      <span key="open-paren" className={`text-gray-600 font-medium self-center ${size === 'small' ? 'text-xs' : 'text-sm'}`}>(</span>
    );
  }

  group.conditions.forEach((condition, index) => {
    // Add logic badge before condition (except for the first one)
    if (index > 0) {
      badges.push(
        <Badge
          key={`logic-${index}`}
          variant="info"
          className={`uppercase font-semibold ${size === 'small' ? 'text-xs' : 'text-xs'}`}
        >
          {group.logic}
        </Badge>
      );
    }

    // Check if it's a nested group or a condition
    if ('logic' in condition) {
      // It's a nested FilterGroup
      const nestedBadges = renderFilterGroup(condition as FilterGroup, fieldLabels, true, size);
      badges.push(...nestedBadges);
    } else {
      // It's a FilterCondition
      badges.push(
        <Badge
          key={`condition-${index}`}
          className={size === 'small' ? 'text-xs' : ''}
        >
          {formatCondition(condition as FilterCondition, fieldLabels)}
        </Badge>
      );
    }
  });

  // Add closing parenthesis for nested groups
  if (isNested) {
    badges.push(
      <span key="close-paren" className={`text-gray-600 font-medium self-center ${size === 'small' ? 'text-xs' : 'text-sm'}`}>)</span>
    );
  }

  return badges;
};
